import {
  type ImageUploadTooltipCopy,
  buildAdminV2ImageUploadTooltip,
} from '@/admin-v2/tools/image-upload-tooltip-copy';
import { validateAdminV2ParameterCalculatorInstance } from '@/admin-v2/tools/parameter-calculator';
import {
  isAdminV2ToolFieldControl,
  resolveAdminV2WorkspaceParamType,
} from '@/admin-v2/tools/tool-field-control-capabilities';
import {
  getAdminV2UserFacingToolFieldDefaultValue,
  getAdminV2UserFacingToolOptions,
  isAdminV2UserFacingToolField,
} from '@/admin-v2/tools/tool-field-visibility';
import { resolveAdminV2ToolDisplay } from '@/admin-v2/tools/tool-localized-copy';
import {
  type AdminV2PublicWorkspaceToolRuntimePackage,
  resolveAdminV2WorkspaceOutputBindings,
  toAdminV2PublicWorkspaceToolIcon,
} from '@/admin-v2/workspace/workspace-runtime';
import type { CmsLocale } from '@/cms/types';
import { workspaceAccessConfig } from '@/config/workspace-access';
import { supportsWorkspaceBatchProcessing } from '@/workspace/batch-policy';
import type { UpscalerSizeCopy } from '@/workspace/code-owned/upscaler-size-copy';
import type {
  WorkspaceStudioGlobalConfig,
  WorkspaceToolGroupView,
} from './contracts/server-model';
import type {
  WorkspaceLayoutVariant,
  WorkspaceToolAccessPolicyRule,
  WorkspaceToolParamScalarValue,
  WorkspaceToolParamValue,
  WorkspaceToolParamView,
  WorkspaceToolPrefillFieldView,
  WorkspaceToolSummaryView,
  WorkspaceToolTemplatePickerView,
  WorkspaceToolView,
  WorkspaceToolViewKind,
} from './contracts/tool-view';

type WorkspaceInputBindings = NonNullable<WorkspaceToolView['inputBindings']>;
type WorkspaceRuntimePackage = AdminV2PublicWorkspaceToolRuntimePackage;
type WorkspaceRuntimeField = WorkspaceRuntimePackage['toolFields'][number];

function resolveLocalizedText(
  value: Record<string, string> | undefined,
  locale: CmsLocale = 'en'
) {
  if (!value) return '';
  const localized = value[locale];
  if (localized?.trim()) return localized;
  throw new Error(
    `Admin v2 published Tool runtime is missing localized text for locale ${locale}.`
  );
}

function readWorkspaceAccessPolicy(
  rule: WorkspaceToolAccessPolicyRule | undefined,
  owner: string
): WorkspaceToolAccessPolicyRule {
  if (!rule) {
    throw new Error(
      `Admin v2 Workspace runtime is missing accessPolicy for ${owner}.`
    );
  }

  return rule;
}

function requiresPaidMarker(rule: WorkspaceToolAccessPolicyRule) {
  return (
    workspaceAccessConfig.paidEntitlementsEnabled &&
    (rule.requiredAccess === 'paid' || rule.requiredAccess === 'plan')
  );
}

function isWorkspaceUserVisibleField(field: WorkspaceRuntimeField) {
  return isAdminV2UserFacingToolField(field);
}

function isWorkspacePrefillField(field: WorkspaceRuntimeField) {
  return isWorkspaceUserVisibleField(field) && field.prefillable === true;
}

function buildWorkspaceToolOptionViews(
  field: WorkspaceRuntimeField,
  locale: CmsLocale
): WorkspaceToolParamView['options'] {
  if (field.control !== 'select' && field.control !== 'multi-select') {
    return undefined;
  }

  return getAdminV2UserFacingToolOptions(field.options).map((option) => {
    const accessPolicy = readWorkspaceAccessPolicy(
      option.accessPolicy,
      `field ${field.id} option ${String(option.value)}`
    );
    return {
      value: option.value,
      label: resolveLocalizedText(option.label, locale) || String(option.value),
      ...(option.labelOverride
        ? {
            labelOverride: {
              ...option.labelOverride,
              label: resolveLocalizedText(option.labelOverride.label, locale),
            },
          }
        : {}),
      infoText: resolveLocalizedText(option.description, locale),
      ...(option.helpPreview ? { helpPreview: option.helpPreview } : {}),
      disabled: option.disabled === true,
      accessPolicy,
      requiredPaid: requiresPaidMarker(accessPolicy),
    };
  });
}

function buildWorkspaceToolTemplatePickerView(
  runtimePackage: WorkspaceRuntimePackage,
  locale: CmsLocale
): WorkspaceToolTemplatePickerView | undefined {
  const config = runtimePackage.workspaceView.templatePicker;
  if (!config) return undefined;

  const fieldById = new Map(
    runtimePackage.toolFields.map((field) => [field.id, field])
  );

  return {
    groups: config.groups.map((group) => {
      const selectionField = fieldById.get(group.selectionFieldId);
      if (!selectionField || selectionField.control !== 'select') {
        throw new Error(
          `Admin v2 Workspace template picker group ${group.id} has no visible select field ${group.selectionFieldId}.`
        );
      }

      const optionViews = buildWorkspaceToolOptionViews(selectionField, locale);
      const optionByValue = new Map(
        (optionViews ?? []).map((option) => [String(option.value), option])
      );
      const templates = group.templates.map((template) => {
        const option = optionByValue.get(template.value);
        if (!option || typeof option.value !== 'string') {
          throw new Error(
            `Admin v2 Workspace template picker group ${group.id} is missing option ${template.value}.`
          );
        }

        return {
          value: option.value,
          label: option.label,
          ...(option.infoText ? { infoText: option.infoText } : {}),
          previewUrl: template.previewUrl,
          width: template.width,
          height: template.height,
          ...(template.animatedPreviewUrl &&
          template.animatedWidth &&
          template.animatedHeight
            ? {
                animatedPreviewUrl: template.animatedPreviewUrl,
                animatedWidth: template.animatedWidth,
                animatedHeight: template.animatedHeight,
              }
            : {}),
          disabled: option.disabled === true,
          accessPolicy: option.accessPolicy,
          requiredPaid: option.requiredPaid,
        };
      });

      const customImageField = group.customImageFieldId
        ? fieldById.get(group.customImageFieldId)
        : undefined;
      if (
        group.customImageFieldId &&
        (!customImageField || customImageField.control !== 'image_file')
      ) {
        throw new Error(
          `Admin v2 Workspace template picker group ${group.id} has no visible custom image field ${group.customImageFieldId}.`
        );
      }

      const customAccessPolicy = customImageField
        ? readWorkspaceAccessPolicy(
            customImageField.accessPolicy,
            `field ${customImageField.id}`
          )
        : undefined;
      const infoText = resolveLocalizedText(selectionField.tooltip, locale);

      return {
        id: group.id,
        label:
          resolveLocalizedText(selectionField.label, locale) ||
          selectionField.id,
        ...(infoText ? { infoText } : {}),
        required: group.required,
        selectionFieldId: group.selectionFieldId,
        ...(customImageField && customAccessPolicy
          ? {
              customImage: {
                fieldId: customImageField.id,
                label:
                  resolveLocalizedText(customImageField.label, locale) ||
                  customImageField.id,
                ...(resolveLocalizedText(customImageField.tooltip, locale)
                  ? {
                      infoText: resolveLocalizedText(
                        customImageField.tooltip,
                        locale
                      ),
                    }
                  : {}),
                allowedMimeTypes: [
                  ...(customImageField.imageConstraints?.allowedMimeTypes ??
                    []),
                ],
                ...(typeof customImageField.imageConstraints?.maxSingleBytes ===
                'number'
                  ? {
                      maxSingleBytes:
                        customImageField.imageConstraints.maxSingleBytes,
                    }
                  : {}),
                requiresClientImageMeta:
                  (customImageField.clientImageMetaRequirements?.fields
                    .length ?? 0) > 0,
                accessPolicy: customAccessPolicy,
                requiredPaid: requiresPaidMarker(customAccessPolicy),
              },
            }
          : {}),
        templates,
      };
    }),
  };
}

function resolveWorkspaceToolFieldType(
  field: WorkspaceRuntimeField
): WorkspaceToolParamView['type'] | null {
  if (!isAdminV2ToolFieldControl(field.control)) {
    throw new Error(
      `Unsupported admin v2 Workspace field control: ${String(field.control)}`
    );
  }

  const type = resolveAdminV2WorkspaceParamType({
    control: field.control,
    inputRole: field.inputRole,
  });
  return type === 'select' &&
    field.workspaceControlVariant === 'segmented-slider'
    ? 'segmented-slider'
    : type;
}

export function buildWorkspaceToolFieldViews(
  toolFields: WorkspaceRuntimePackage['toolFields'],
  locale: CmsLocale = 'en',
  upscaleSizeCopy?: UpscalerSizeCopy
): WorkspaceToolParamView[] {
  if (
    !upscaleSizeCopy &&
    toolFields.some(
      (field) =>
        isWorkspaceUserVisibleField(field) &&
        field.workspaceControlVariant === 'upscale-size'
    )
  )
    throw new Error('Workspace requires server-resolved Upscaler size copy.');
  return toolFields.filter(isWorkspaceUserVisibleField).flatMap((field) => {
    const type = resolveWorkspaceToolFieldType(field);
    if (!type) return [];
    const placeholder = resolveLocalizedText(field.placeholder, locale);
    const fieldAccessPolicy = readWorkspaceAccessPolicy(
      field.accessPolicy,
      `field ${field.id}`
    );

    return [
      {
        id: field.id,
        type,
        group: field.workspaceGroup ?? 'main',
        ...(field.activeWhen ? { activeWhen: field.activeWhen } : {}),
        ...(field.workspaceControlVariant === 'stepped-slider' ||
        field.workspaceControlVariant === 'tabs' ||
        field.workspaceControlVariant === 'upscale-size'
          ? { controlVariant: field.workspaceControlVariant }
          : {}),
        ...(field.workspaceControlVariant === 'upscale-size'
          ? { upscaleSizeCopy }
          : {}),
        label: resolveLocalizedText(field.label, locale) || field.id,
        infoText: resolveLocalizedText(field.tooltip, locale),
        ...(field.helpPreview ? { helpPreview: field.helpPreview } : {}),
        defaultValue: getAdminV2UserFacingToolFieldDefaultValue(field),
        ...(placeholder ? { placeholder } : {}),
        ...(typeof field.min === 'number' ? { min: field.min } : {}),
        ...(typeof field.max === 'number' ? { max: field.max } : {}),
        ...(typeof field.step === 'number' ? { step: field.step } : {}),
        ...(field.workspaceSliderSnapPoints
          ? { sliderSnapPoints: [...field.workspaceSliderSnapPoints] }
          : {}),
        options: buildWorkspaceToolOptionViews(field, locale),
        accessPolicy: fieldAccessPolicy,
        requiredPaid: requiresPaidMarker(fieldAccessPolicy),
      },
    ];
  });
}

function buildWorkspaceToolPrefillFieldViews(
  toolFields: WorkspaceRuntimePackage['toolFields'],
  locale: CmsLocale
): WorkspaceToolPrefillFieldView[] {
  return toolFields.filter(isWorkspacePrefillField).map((field) => {
    if (!isAdminV2ToolFieldControl(field.control)) {
      throw new Error(
        `Unsupported admin v2 Workspace prefill field control: ${String(field.control)}`
      );
    }
    if (field.control === 'image' || field.control === 'file') {
      throw new Error(
        `Admin v2 Workspace prefill does not support field control ${field.control}: ${field.id}`
      );
    }

    const accessPolicy = readWorkspaceAccessPolicy(
      field.accessPolicy,
      `field ${field.id}`
    );
    const defaultValue = getAdminV2UserFacingToolFieldDefaultValue(field);
    if (
      defaultValue !== undefined &&
      !isWorkspaceToolParamValue(defaultValue)
    ) {
      throw new Error(
        `Admin v2 Workspace prefill field ${field.id} has an invalid default value.`
      );
    }
    const placeholder = resolveLocalizedText(field.placeholder, locale);
    const inputRole = field.inputRole ?? 'parameter';

    return {
      id: field.id,
      control: field.control,
      inputRole,
      required: field.required,
      prefillable: true,
      allowBatch: field.allowBatch === true,
      label: resolveLocalizedText(field.label, locale) || field.id,
      infoText: resolveLocalizedText(field.tooltip, locale),
      ...(placeholder ? { placeholder } : {}),
      ...(defaultValue !== undefined ? { defaultValue } : {}),
      ...(typeof field.min === 'number' ? { min: field.min } : {}),
      ...(typeof field.max === 'number' ? { max: field.max } : {}),
      ...(typeof field.step === 'number' ? { step: field.step } : {}),
      ...(typeof field.minLength === 'number'
        ? { minLength: field.minLength }
        : {}),
      ...(typeof field.maxLength === 'number'
        ? { maxLength: field.maxLength }
        : {}),
      ...(typeof field.minItems === 'number'
        ? { minItems: field.minItems }
        : {}),
      ...(typeof field.maxItems === 'number'
        ? { maxItems: field.maxItems }
        : {}),
      accessPolicy,
      requiredPaid: requiresPaidMarker(accessPolicy),
      options: buildWorkspaceToolOptionViews(field, locale),
      ...(field.imageConstraints
        ? {
            imageConstraints: {
              allowedMimeTypes: [
                ...(field.imageConstraints.allowedMimeTypes ?? []),
              ],
              ...(typeof field.imageConstraints.maxCount === 'number'
                ? { maxCount: field.imageConstraints.maxCount }
                : {}),
              ...(typeof field.imageConstraints.maxSingleBytes === 'number'
                ? { maxSingleBytes: field.imageConstraints.maxSingleBytes }
                : {}),
              ...(typeof field.imageConstraints.maxTotalBytes === 'number'
                ? { maxTotalBytes: field.imageConstraints.maxTotalBytes }
                : {}),
              requireKnownContentLength:
                field.imageConstraints.requireKnownContentLength === true,
              ...(typeof field.imageConstraints.minWidth === 'number'
                ? { minWidth: field.imageConstraints.minWidth }
                : {}),
              ...(typeof field.imageConstraints.minHeight === 'number'
                ? { minHeight: field.imageConstraints.minHeight }
                : {}),
              ...(typeof field.imageConstraints.maxWidth === 'number'
                ? { maxWidth: field.imageConstraints.maxWidth }
                : {}),
              ...(typeof field.imageConstraints.maxHeight === 'number'
                ? { maxHeight: field.imageConstraints.maxHeight }
                : {}),
              ...(typeof field.imageConstraints.maxMegapixels === 'number'
                ? { maxMegapixels: field.imageConstraints.maxMegapixels }
                : {}),
              colorModes: [...(field.imageConstraints.colorModes ?? [])],
            },
          }
        : {}),
      ...(field.mediaConstraints
        ? {
            mediaConstraints: {
              allowedMimeTypes: [...field.mediaConstraints.allowedMimeTypes],
              ...(typeof field.mediaConstraints.maxCount === 'number'
                ? { maxCount: field.mediaConstraints.maxCount }
                : {}),
              ...(typeof field.mediaConstraints.maxSingleBytes === 'number'
                ? { maxSingleBytes: field.mediaConstraints.maxSingleBytes }
                : {}),
              ...(typeof field.mediaConstraints.maxTotalBytes === 'number'
                ? { maxTotalBytes: field.mediaConstraints.maxTotalBytes }
                : {}),
              ...(typeof field.mediaConstraints.minSingleDurationSeconds ===
              'number'
                ? {
                    minSingleDurationSeconds:
                      field.mediaConstraints.minSingleDurationSeconds,
                  }
                : {}),
              ...(typeof field.mediaConstraints.maxSingleDurationSeconds ===
              'number'
                ? {
                    maxSingleDurationSeconds:
                      field.mediaConstraints.maxSingleDurationSeconds,
                  }
                : {}),
              ...(typeof field.mediaConstraints.maxTotalDurationSeconds ===
              'number'
                ? {
                    maxTotalDurationSeconds:
                      field.mediaConstraints.maxTotalDurationSeconds,
                  }
                : {}),
              ...(typeof field.mediaConstraints.minVideoShortEdge === 'number'
                ? {
                    minVideoShortEdge: field.mediaConstraints.minVideoShortEdge,
                  }
                : {}),
              ...(typeof field.mediaConstraints.maxVideoShortEdge === 'number'
                ? {
                    maxVideoShortEdge: field.mediaConstraints.maxVideoShortEdge,
                  }
                : {}),
            },
          }
        : {}),
    };
  });
}

function isWorkspaceToolParamScalarValue(
  value: unknown
): value is WorkspaceToolParamScalarValue {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

function isWorkspaceToolParamValue(
  value: unknown
): value is WorkspaceToolParamValue {
  if (Array.isArray(value)) {
    return value.every(isWorkspaceToolParamScalarValue);
  }
  return isWorkspaceToolParamScalarValue(value);
}

export function resolveWorkspaceInputBindings(
  runtimePackage: Pick<WorkspaceRuntimePackage, 'toolId' | 'toolFields'> & {
    workspaceView: {
      templatePicker?: {
        groups: Array<{
          id: string;
          required: boolean;
          selectionFieldId: string;
          customImageFieldId?: string;
        }>;
      };
    };
  }
): WorkspaceInputBindings {
  const visibleFields = runtimePackage.toolFields.filter(
    isWorkspaceUserVisibleField
  );
  const templatePickerGroups =
    runtimePackage.workspaceView.templatePicker?.groups ?? [];
  const templateCustomImageFieldIds = new Set(
    templatePickerGroups.flatMap((group) =>
      group.customImageFieldId ? [group.customImageFieldId] : []
    )
  );
  const imageFields = visibleFields.filter(
    (field) =>
      field.control === 'image_file' &&
      !templateCustomImageFieldIds.has(field.id)
  );
  const explicitPrimaryImageFields = imageFields.filter(
    (field) => field.inputRole === 'primaryImage'
  );
  if (explicitPrimaryImageFields.length > 1) {
    throw new Error(
      `Admin v2 Tool ${runtimePackage.toolId} must define at most one primaryImage field.`
    );
  }
  const implicitPrimaryImageFields = imageFields.filter(
    (field) =>
      !['referenceImage', 'firstFrame', 'lastFrame'].includes(
        field.inputRole ?? 'parameter'
      )
  );
  const primaryImageField =
    explicitPrimaryImageFields[0] ?? implicitPrimaryImageFields[0] ?? null;
  const firstFrameFields = imageFields.filter(
    (field) => field.inputRole === 'firstFrame'
  );
  const lastFrameFields = imageFields.filter(
    (field) => field.inputRole === 'lastFrame'
  );
  if (firstFrameFields.length > 1 || lastFrameFields.length > 1) {
    throw new Error(
      `Admin v2 Tool ${runtimePackage.toolId} must define at most one firstFrame and one lastFrame field.`
    );
  }
  const referenceVideoFields = visibleFields.filter(
    (field) =>
      field.control === 'video_file' && field.inputRole === 'referenceVideo'
  );
  const referenceAudioFields = visibleFields.filter(
    (field) =>
      field.control === 'audio_file' && field.inputRole === 'referenceAudio'
  );
  const promptFieldId =
    visibleFields.find(
      (field) => field.control === 'text' && field.inputRole === 'prompt'
    )?.id ??
    visibleFields.find((field) => field.control === 'text')?.id ??
    null;
  const primaryImageFieldId = primaryImageField?.id ?? null;

  return {
    primaryImageFieldId,
    promptFieldId,
    referenceImageFieldIds: imageFields
      .filter((field) =>
        field.inputRole === 'referenceImage'
          ? true
          : field.inputRole === 'firstFrame' || field.inputRole === 'lastFrame'
            ? false
            : field.id !== primaryImageFieldId
      )
      .map((field) => field.id),
    firstFrameFieldId: firstFrameFields[0]?.id ?? null,
    lastFrameFieldId: lastFrameFields[0]?.id ?? null,
    referenceVideoFieldIds: referenceVideoFields.map((field) => field.id),
    referenceAudioFieldIds: referenceAudioFields.map((field) => field.id),
    ...(templatePickerGroups.length > 0
      ? {
          templatePickerGroups: templatePickerGroups.map((group) => ({
            id: group.id,
            required: group.required,
            selectionFieldId: group.selectionFieldId,
            ...(group.customImageFieldId
              ? { customImageFieldId: group.customImageFieldId }
              : {}),
          })),
        }
      : {}),
  };
}

export function resolveWorkspaceToolViewKind(params: {
  toolId: string;
  layoutVariant: WorkspaceLayoutVariant;
}): WorkspaceToolViewKind {
  if (params.layoutVariant === 'composer') {
    return 'reference-composer';
  }

  if (params.layoutVariant === 'primary-with-reference') {
    return 'primary-with-reference';
  }

  return 'primary-input-form';
}

function resolveImageMimeRuntimeCaps(
  primaryImageField?: WorkspaceRuntimeField | null
) {
  const allowedMimeTypes =
    primaryImageField?.imageConstraints?.allowedMimeTypes ?? [];
  if (allowedMimeTypes.length === 0) {
    return {
      acceptedFileLabels: [],
      accept: '',
    };
  }

  return {
    acceptedFileLabels: allowedMimeTypes,
    accept: allowedMimeTypes.join(','),
  };
}

function mergeFieldImageConstraint(
  field: WorkspaceRuntimeField | null | undefined,
  key: keyof NonNullable<WorkspaceRuntimeField['imageConstraints']>,
  fallback?: number
) {
  const value = field?.imageConstraints?.[key];
  return typeof value === 'number' ? value : fallback;
}

function buildWorkspaceToolView(
  runtimePackage: WorkspaceRuntimePackage,
  _workspaceConfig: Pick<
    WorkspaceStudioGlobalConfig,
    'referenceImagesLabel' | 'uploadLabel'
  >,
  locale: CmsLocale,
  upscaleSizeCopy?: UpscalerSizeCopy,
  imageUploadTooltipCopy?: ImageUploadTooltipCopy
): WorkspaceToolView {
  if (!runtimePackage.workspaceView.enabled) {
    throw new Error(
      `Admin v2 Tool runtime package is not workspace enabled: ${runtimePackage.toolId}`
    );
  }

  const bindings = resolveWorkspaceInputBindings(runtimePackage);
  const layoutVariant = runtimePackage.workspaceView.layoutVariant;
  const fieldById = new Map(
    runtimePackage.toolFields.map((field) => [field.id, field])
  );
  const primaryImageField = bindings.primaryImageFieldId
    ? fieldById.get(bindings.primaryImageFieldId)
    : null;
  const promptField = bindings.promptFieldId
    ? fieldById.get(bindings.promptFieldId)
    : null;
  const referenceImageFields = bindings.referenceImageFieldIds
    .map((fieldId) => fieldById.get(fieldId))
    .filter((field): field is WorkspaceRuntimeField => !!field);
  const firstFrameField = bindings.firstFrameFieldId
    ? fieldById.get(bindings.firstFrameFieldId)
    : undefined;
  const lastFrameField = bindings.lastFrameFieldId
    ? fieldById.get(bindings.lastFrameFieldId)
    : undefined;
  const referenceVideoFields = (bindings.referenceVideoFieldIds ?? [])
    .map((fieldId) => fieldById.get(fieldId))
    .filter((field): field is WorkspaceRuntimeField => !!field);
  const referenceAudioFields = (bindings.referenceAudioFieldIds ?? [])
    .map((fieldId) => fieldById.get(fieldId))
    .filter((field): field is WorkspaceRuntimeField => !!field);
  const templateCustomImageFields = (
    bindings.templatePickerGroups ?? []
  ).flatMap((group) => {
    const field = group.customImageFieldId
      ? fieldById.get(group.customImageFieldId)
      : undefined;
    return field ? [field] : [];
  });
  const uploadConstraintField = primaryImageField ?? referenceImageFields[0];
  const clientImageMetaFields = Array.from(
    new Set(
      [
        primaryImageField,
        ...referenceImageFields,
        firstFrameField,
        lastFrameField,
        ...templateCustomImageFields,
      ].flatMap((field) => field?.clientImageMetaRequirements?.fields ?? [])
    )
  );
  const effectiveImageConstraints =
    runtimePackage.clientInputLimits.imageConstraints;
  const maxPromptCharacters =
    runtimePackage.clientInputLimits.maxPromptCharacters;
  const mimeCaps = resolveImageMimeRuntimeCaps(uploadConstraintField);
  const maxSingleBytes =
    mergeFieldImageConstraint(
      uploadConstraintField,
      'maxSingleBytes',
      effectiveImageConstraints.paidMaxBytes
    ) ?? effectiveImageConstraints.paidMaxBytes;
  const maxTotalBytes =
    mergeFieldImageConstraint(
      uploadConstraintField,
      'maxTotalBytes',
      maxSingleBytes
    ) ?? maxSingleBytes;
  const maxReferenceImages = referenceImageFields.reduce(
    (sum, field) => sum + (field.imageConstraints?.maxCount ?? 1),
    0
  );
  const maxReferenceVideos = referenceVideoFields.reduce(
    (sum, field) => sum + (field.mediaConstraints?.maxCount ?? 1),
    0
  );
  const maxReferenceAudios = referenceAudioFields.reduce(
    (sum, field) => sum + (field.mediaConstraints?.maxCount ?? 1),
    0
  );
  const display = resolveAdminV2ToolDisplay({
    display: runtimePackage.display,
    locale,
    fieldPath: `workspace.tools.${runtimePackage.toolId}.display`,
  });
  const icon = toAdminV2PublicWorkspaceToolIcon(display.icon);
  const parameterCalculatorCandidate =
    runtimePackage.workspaceView.parameterCalculators?.[0];
  const parameterCalculatorIssues = parameterCalculatorCandidate
    ? validateAdminV2ParameterCalculatorInstance({
        instance: parameterCalculatorCandidate,
        toolFields: runtimePackage.toolFields,
      })
    : [];
  let parameterCalculator: WorkspaceToolView['parameterCalculator'];
  if (parameterCalculatorCandidate && parameterCalculatorIssues.length === 0) {
    try {
      parameterCalculator = {
        ...parameterCalculatorCandidate,
        copy: Object.fromEntries(
          parameterCalculatorCandidate.copy.map((item) => [
            item.id,
            resolveLocalizedText(item.text, locale),
          ])
        ),
      };
    } catch (error) {
      parameterCalculatorIssues.push({
        message: error instanceof Error ? error.message : String(error),
        path: ['copy'],
      });
    }
  }
  if (parameterCalculatorCandidate && parameterCalculatorIssues.length > 0) {
    console.error('[workspace] parameter calculator runtime unavailable', {
      toolId: runtimePackage.toolId,
      calculatorId: parameterCalculatorCandidate.id,
      issues: parameterCalculatorIssues,
    });
  }
  const templatePicker = buildWorkspaceToolTemplatePickerView(
    runtimePackage,
    locale
  );
  const requiredTemplateDefaultByFieldId = new Map(
    (templatePicker?.groups ?? []).flatMap((group) => {
      if (!group.required) return [];
      const firstAvailableTemplate = group.templates.find(
        (template) => !template.disabled
      );
      return firstAvailableTemplate
        ? [[group.selectionFieldId, firstAvailableTemplate.value] as const]
        : [];
    })
  );
  const fields = buildWorkspaceToolFieldViews(
    runtimePackage.toolFields,
    locale,
    upscaleSizeCopy
  ).map((field) => {
    const requiredDefault = requiredTemplateDefaultByFieldId.get(field.id);
    return requiredDefault === undefined
      ? field
      : { ...field, defaultValue: requiredDefault };
  });

  return {
    toolId: runtimePackage.toolId,
    surfaceMode: runtimePackage.workspaceView.surfaceMode,
    toolVersionToken: runtimePackage.toolVersionId,
    label: display.name,
    description: display.summary ?? '',
    ...(icon ? { icon } : {}),
    ...(display.badge ? { badgeLabel: display.badge } : {}),
    accessPolicy: readWorkspaceAccessPolicy(
      runtimePackage.accessPolicy.tool,
      `tool ${runtimePackage.toolId}`
    ),
    requiredPaid: requiresPaidMarker(runtimePackage.accessPolicy.tool),
    creditPolicy: runtimePackage.userCreditPolicy,
    showcase: runtimePackage.workspaceView.showcase ?? null,
    viewKind: resolveWorkspaceToolViewKind({
      toolId: runtimePackage.toolId,
      layoutVariant,
    }),
    inputBindings: bindings,
    outputBindings: resolveAdminV2WorkspaceOutputBindings(runtimePackage),
    runtimeCaps: {
      creditCalculationMode: runtimePackage.creditCalculationMode,
      layoutVariant,
      primaryImageRequired: primaryImageField?.required === true,
      promptRequired: promptField?.required === true,
      referenceImagesRequired: bindings.referenceImageFieldIds.some(
        (fieldId) => fieldById.get(fieldId)?.required === true
      ),
      maxReferenceImages,
      maxReferenceVideos,
      maxReferenceAudios,
      maxPromptCharacters,
      maxSingleBytes,
      maxTotalBytes,
      freeMaxSingleBytes: Math.min(
        effectiveImageConstraints.freeMaxBytes,
        maxSingleBytes
      ),
      paidMaxSingleBytes: maxSingleBytes,
      minImageWidth: mergeFieldImageConstraint(
        primaryImageField,
        'minWidth',
        effectiveImageConstraints.minWidth
      ),
      minImageHeight: mergeFieldImageConstraint(
        primaryImageField,
        'minHeight',
        effectiveImageConstraints.minHeight
      ),
      maxImageWidth: mergeFieldImageConstraint(
        primaryImageField,
        'maxWidth',
        effectiveImageConstraints.maxWidth
      ),
      maxImageHeight: mergeFieldImageConstraint(
        primaryImageField,
        'maxHeight',
        effectiveImageConstraints.maxHeight
      ),
      maxImageMegapixels: mergeFieldImageConstraint(
        primaryImageField,
        'maxMegapixels',
        effectiveImageConstraints.maxMegapixels
      ),
      ...(runtimePackage.clientInputLimits.imageConstraintRules?.length
        ? {
            imageInputConstraintRules:
              runtimePackage.clientInputLimits.imageConstraintRules.map(
                (rule) => ({
                  activeWhen: structuredClone(rule.activeWhen),
                  ...(rule.imageConstraints.maxWidth
                    ? { maxImageWidth: rule.imageConstraints.maxWidth }
                    : {}),
                  ...(rule.imageConstraints.maxHeight
                    ? { maxImageHeight: rule.imageConstraints.maxHeight }
                    : {}),
                  ...(rule.imageConstraints.maxMegapixels
                    ? {
                        maxImageMegapixels: rule.imageConstraints.maxMegapixels,
                      }
                    : {}),
                })
              ),
          }
        : {}),
      batchProcessingSupported: supportsWorkspaceBatchProcessing(
        runtimePackage.toolId
      ),
      repeatGenerationMaxJobs:
        runtimePackage.workspaceView.repeatGeneration?.maxJobs ?? null,
      referenceImageInputKey: bindings.referenceImageFieldIds[0] ?? null,
      acceptedFileLabels: mimeCaps.acceptedFileLabels,
      accept: mimeCaps.accept,
      ...(clientImageMetaFields.length > 0
        ? { clientImageMetaRequirements: { fields: clientImageMetaFields } }
        : {}),
      outputTransparentBackground:
        runtimePackage.workspaceView.output?.transparentBackground === true ||
        runtimePackage.outputContract.transparentBackground === true,
    },
    inputLabels: runtimePackage.workspaceView.inputLabels
      ? {
          primaryImage:
            resolveLocalizedText(
              runtimePackage.workspaceView.inputLabels.primaryImage,
              locale
            ) || _workspaceConfig.uploadLabel,
          referenceImage:
            resolveLocalizedText(
              runtimePackage.workspaceView.inputLabels.referenceImage,
              locale
            ) || _workspaceConfig.referenceImagesLabel,
          frameMode:
            resolveLocalizedText(
              runtimePackage.workspaceView.inputLabels.frameMode,
              locale
            ) || 'Frames',
        }
      : undefined,
    actionBindings: runtimePackage.workspaceView.actionBindings,
    toolbar: runtimePackage.workspaceView.toolbar,
    ...(parameterCalculator
      ? {
          parameterCalculator,
        }
      : {}),
    ...(templatePicker ? { templatePicker } : {}),
    fields,
    prefillFields: buildWorkspaceToolPrefillFieldViews(
      runtimePackage.toolFields,
      locale
    ).map((field) => {
      if (field.control !== 'image_file') return field;
      if (field.infoText) return field;

      const paidLimit = Math.min(
        maxSingleBytes,
        field.imageConstraints?.maxSingleBytes ?? maxSingleBytes
      );
      const freeLimit = workspaceAccessConfig.paidEntitlementsEnabled
        ? Math.min(effectiveImageConstraints.freeMaxBytes, paidLimit)
        : paidLimit;

      return {
        ...field,
        infoText: buildAdminV2ImageUploadTooltip(
          {
            maxImages: field.imageConstraints?.maxCount,
            freeMaxBytes: freeLimit,
            paidMaxBytes: paidLimit,
          },
          requireUploadTooltipCopy(imageUploadTooltipCopy)
        ),
      };
    }),
  };
}

export function buildDefaultWorkspaceToolParams(
  fields: WorkspaceToolParamView[]
) {
  return Object.fromEntries(
    fields
      .filter((field) => isWorkspaceToolParamValue(field.defaultValue))
      .map((field) => [field.id, field.defaultValue])
  ) as Record<string, WorkspaceToolParamValue>;
}

function requireUploadTooltipCopy(copy: ImageUploadTooltipCopy | undefined) {
  if (!copy)
    throw new Error(
      'Workspace requires server-resolved image upload tooltip copy.'
    );
  return copy;
}

export function buildWorkspaceToolViews(params: {
  locale: CmsLocale;
  workspaceConfig: WorkspaceStudioGlobalConfig;
  runtimePackages: WorkspaceRuntimePackage[];
  upscaleSizeCopy?: UpscalerSizeCopy;
  imageUploadTooltipCopy?: ImageUploadTooltipCopy;
}): WorkspaceToolView[] {
  const tools = params.runtimePackages.map((runtimePackage) =>
    buildWorkspaceToolView(
      runtimePackage,
      params.workspaceConfig,
      params.locale,
      params.upscaleSizeCopy,
      params.imageUploadTooltipCopy
    )
  );

  if (tools.length === 0) {
    throw new Error(
      'Workspace requires at least one admin v2 Tool runtime package'
    );
  }

  return tools;
}

export function buildWorkspaceToolSummaryViews(params: {
  runtimePackages: WorkspaceRuntimePackage[];
  locale: CmsLocale;
  toolBadgeLabels?: ReadonlyMap<string, string>;
}): WorkspaceToolSummaryView[] {
  return params.runtimePackages.map((runtimePackage) => {
    const display = resolveAdminV2ToolDisplay({
      display: runtimePackage.display,
      locale: params.locale,
      fieldPath: `workspace.tools.${runtimePackage.toolId}.display`,
    });
    const icon = toAdminV2PublicWorkspaceToolIcon(display.icon);
    const badgeLabel =
      display.badge ?? params.toolBadgeLabels?.get(runtimePackage.toolId);

    return {
      toolId: runtimePackage.toolId,
      surfaceMode: runtimePackage.workspaceView.surfaceMode,
      mediaKind:
        runtimePackage.outputContract.kind === 'video' ? 'video' : 'image',
      label: display.name,
      ...(icon ? { icon } : {}),
      ...(display.summary ? { summary: display.summary } : {}),
      badgeLabel,
      accessPolicy: readWorkspaceAccessPolicy(
        runtimePackage.accessPolicy.tool,
        `tool ${runtimePackage.toolId}`
      ),
      requiredPaid: requiresPaidMarker(runtimePackage.accessPolicy.tool),
      ...(runtimePackage.userCreditPolicy.paidAccessDiscount
        ? {
            paidAccessDiscount: structuredClone(
              runtimePackage.userCreditPolicy.paidAccessDiscount
            ),
          }
        : {}),
    };
  });
}

export function buildWorkspaceToolGroups(params: {
  workspaceConfig: WorkspaceStudioGlobalConfig;
  tools: WorkspaceToolSummaryView[];
}): WorkspaceToolGroupView[] {
  const label =
    params.workspaceConfig.toolCategoryLabels['image-tools'] ??
    Object.values(params.workspaceConfig.toolCategoryLabels).find((value) =>
      value.trim()
    );

  if (!label || params.tools.length === 0) {
    return [];
  }

  return [
    {
      id: 'admin-v2-tools',
      label,
      tools: params.tools.map((tool) => ({
        toolId: tool.toolId,
        label: tool.label,
        icon: tool.icon ?? '',
        badgeLabel: tool.badgeLabel ?? '',
      })),
    },
  ];
}

export function getWorkspaceTransparentOutputToolIds(
  runtimePackages: WorkspaceRuntimePackage[]
) {
  return runtimePackages
    .filter(
      (runtimePackage) =>
        runtimePackage.workspaceView.output?.transparentBackground === true ||
        runtimePackage.outputContract.transparentBackground === true
    )
    .map((runtimePackage) => runtimePackage.toolId);
}

export function buildWorkspaceToolViewByToolId(params: {
  toolId: string;
  locale: CmsLocale;
  workspaceConfig: Pick<
    WorkspaceStudioGlobalConfig,
    'referenceImagesLabel' | 'uploadLabel'
  >;
  runtimePackages: WorkspaceRuntimePackage[];
  upscaleSizeCopy?: UpscalerSizeCopy;
  imageUploadTooltipCopy?: ImageUploadTooltipCopy;
}): WorkspaceToolView {
  const runtimePackage = params.runtimePackages.find(
    (candidate) => candidate.toolId === params.toolId
  );
  if (!runtimePackage) {
    throw new Error(
      `Workspace admin v2 runtime package is missing toolId: ${params.toolId}`
    );
  }

  return buildWorkspaceToolView(
    runtimePackage,
    params.workspaceConfig,
    params.locale,
    params.upscaleSizeCopy,
    params.imageUploadTooltipCopy
  );
}

export function resolveWorkspaceInitialToolId(params: {
  tools: Array<{ toolId: string }>;
  defaultToolId?: string;
}) {
  if (!params.defaultToolId) {
    throw new Error('Workspace defaultToolId is required');
  }

  if (params.tools.some((tool) => tool.toolId === params.defaultToolId)) {
    return params.defaultToolId;
  }

  throw new Error(
    `Workspace defaultToolId is not backed by a published admin v2 Tool runtime package: ${params.defaultToolId}`
  );
}
